import { memo, useCallback } from "react";

import { ChevronLeft } from "@monta/icons";
import { CheckBox } from "@monta/design-system";

import type { ChangeEvent } from "react";
import type { TreeNodeProps } from "./types";

const TreeNode = memo(
  ({
    node,
    style,
    isChecked,
    isIndeterminate,
    checkedChildrenCount = 0,
    showCount = false,
    onCheckboxChange
  }: TreeNodeProps) => {
    const nodeData = node.data;
    const hasChildren = nodeData.children && nodeData.children.length > 0;

    // * Memoized handlers to prevent recreating on each render
    const handleToggle = useCallback(
      (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        node.toggle();
      },
      [node]
    );

    const handleCheckboxChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onCheckboxChange(nodeData.id, e.target.checked);
      },
      [nodeData.id, onCheckboxChange]
    );

    const handleRowClick = useCallback(
      (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        if (hasChildren) {
          node.toggle();
        }
      },
      [hasChildren, node]
    );

    return (
      <button
        style={style}
        className="w-full flex items-center mb-4 rounded transition-colors duration-150 cursor-pointer group"
        onClick={handleRowClick}
      >
        {/* Expand/Collapse Arrow */}
        <div className="mr-3 w-5 h-5">
          {hasChildren && (
            <button
              onClick={handleToggle}
              className="text-neutral-600 hover:text-neutral-700 transition-colors"
              aria-label={node.isOpen ? "Collapse" : "Expand"}
            >
              <ChevronLeft
                size={node.level === 0 ? "20" : "16"}
                className={`transition-transform duration-200 text-neutral-600 ${
                  node.isOpen ? "rotate-270" : ""
                }`}
                color="currentColor"
              />
            </button>
          )}
        </div>

        {/* Checkbox */}
        <button
          className="mr-2 flex items-center rounded-md -m-1 p-1 transition duration-150 ease-out hover:bg-neutral-100 active:bg-neutral-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-200"
          onClick={(e) => e.stopPropagation()}
          aria-label={nodeData.name}
          type="button"
        >
          <CheckBox
            label={nodeData.name}
            labelClass="sm:text-xs lg:text-sm font-medium text-neutral-700 transition duration-150 ease-out  hover:text-orange-600"
            checkboxBorderColor="#D0D5DD"
            value={nodeData.id}
            aria-label={nodeData.name}
            checked={isChecked}
            onChange={handleCheckboxChange}
            indeterminate={isIndeterminate}
            onClick={(e) => e.stopPropagation()}
            size={node.level === 0 ? 20 : 16}
          />
        </button>

        {/* Optional: Show count of checked children */}
        {hasChildren && nodeData.children && showCount && (
          <span className="mr-2 text-xs text-gray-500">
            ({checkedChildrenCount}/{nodeData.children.length})
          </span>
        )}
      </button>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isChecked === nextProps.isChecked &&
      prevProps.isIndeterminate === nextProps.isIndeterminate &&
      prevProps.checkedChildrenCount === nextProps.checkedChildrenCount &&
      prevProps.node.isOpen === nextProps.node.isOpen &&
      prevProps.style === nextProps.style
    );
  }
);

TreeNode.displayName = "TreeNode";

export default TreeNode;
